import ApplicationController from "./application_controller"
import {gtmTrack, virtualPageView} from "../helpers/gtm"
import {jwUrl} from "../services/jwplatform"
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import lgFullscreen from "lightgallery/plugins/fullscreen";
import lgVideo from "lightgallery/plugins/video";
import videojs from "video.js";

const LINK_ID = "lightgallery-css"
const LG_CONTAINER = ".lg-container"
const LG_CONTENT = ".lg-content"
const LG_THUMB_OUTER = ".lg-thumb-outer"

export default class extends ApplicationController {
  static values = {dynamicEl: Array, adOptions: Object}
  
  connect() {
    // lg.destroy() is async, so does not clean up before the DOM is cached by Turbo
    // if loading from Turbo cache, we need to ensure a clean slate
    document.querySelectorAll(LG_CONTAINER).forEach((e) => e.parentNode.removeChild(e))
    
    if (this.isPreview) return
    
    const link = window.document.createElement("link")
    link.rel = "stylesheet"
    link.href = "https://cdnjs.cloudflare.com/ajax/libs/lightgallery/2.8.1/css/lightgallery-bundle.min.css"
    link.id = LINK_ID
    window.document.head.appendChild(link)
  }
  
  disconnect() {
    if (this.gallery) {
      this.gallery.destroy()
    }
  }
  
  async open(event) {
    if (this.isPreview) return
    const slide = event.type === "gallery:open" ? event.detail : 0
    if (this.gallery) {
      this.gallery.openGallery(slide)
    } else {
      const self = this
      
      const slides = await Promise.all(this.dynamicElValue.map(async (slide) => {
        if (slide.jwId) {
          let updatedSlide
          try {
            const src = await jwUrl(slide.jwId)
            updatedSlide = {
              ...slide,
              video: {
                source: [
                  {
                    src,
                    type: "video/mp4"
                  }
                ],
                attributes: {
                  preload: "none",
                  controls: true
                }
              }
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error)
          }
          return updatedSlide
        } else {
          return slide;
        }
      }));
      
      if (slides.length) {
        window.videojs = videojs.default
        self.gallery = lightGallery(self.element, {
          /* global */
          addClass: "wc-gallery-ad", // out-specifies the bundle's `.lg-outer .lg-content` — see gallery.css
          container: document.body,
          dynamic: true,
          dynamicEl: slides.filter(Boolean),
          licenseKey: "NFA4E-FUF98-3C2M7-UECC2",
          mobileSettings: {
            controls: true, showCloseIcon: true, download: true
          },
          plugins: [lgFullscreen, lgThumbnail, lgVideo, lgZoom],
          
          /* zoom plugin */
          actualSize: false,
          showZoomInOutIcons: true,
          
          /* video plugin */
          videojs: true,
          videojsOptions: {
            autoplay: true, preload: "auto"
          }, /* thumbnails plugin */
          allowMediaOverlap: true,
          toggleThumb: true,
          thumbWidth: 96, // [sic] this is a number
          thumbHeight: "76px" // [sic] this is a string
        })
        
        self.teachGalleryAboutAdStrip()
        self.gallery.openGallery(slide)
      }
    }
  }
  
  galleryAdPlacement() {
    return document.getElementById("gallerybanner-1")
  }

  /**
   * Teach lightGallery the real bounds of the media area.
   *
   * lightGallery sizes video slides itself: available height is
   * `outer.height() - (top + bottom)` from getMediaContainerPosition(), then a uniform
   * scale down to fit. Under `allowMediaOverlap` that method short-circuits to
   * {top: 0, bottom: 0}, so it measures against the whole viewport and centres the video
   * in it — ignoring both the ad strip we reserve at the top and the thumbnail rail that
   * overlays the bottom.
   *
   * Reporting both makes lg size and centre the video within the band that is actually
   * visible, which is what keeps it unclipped and correctly proportioned at any height.
   *
   * Only lg's *video* sizing reads these numbers — images size to .lg-content through CSS.
   * Note that closing .lg-content's bottom edge (gallery.css) does re-centre images too,
   * since it changes the box they fill; that is the same bug being fixed, not a side
   * effect confined to video.
   *
   * Desktop only, checked live rather than at open time so it tracks the CSS media query
   * across a resize. Off desktop the original values pass through untouched.
   *
   * These are lightGallery internals, so they are feature-detected: if an upgrade renames
   * them, video sizing quietly reverts to current behaviour rather than throwing.
   */
  teachGalleryAboutAdStrip() {
    if (typeof this.gallery?.getMediaContainerPosition !== "function") return

    const original = this.gallery.getMediaContainerPosition.bind(this.gallery)

    this.gallery.getMediaContainerPosition = () => {
      const position = original()
      if (!this.isDesktop()) return position

      const rail = this.thumbnailRailHeight()

      // Publish the rail height so gallery.css can close .lg-content's bottom edge.
      // lg's `bottom` only feeds its size calculation — the media is *centred* by CSS
      // inside .lg-content, which otherwise runs to the viewport bottom behind the rail.
      // Reporting it here and closing the box there keeps sizing and centring agreed.
      document.documentElement.style.setProperty("--gallery-rail-height", `${rail}px`)

      return {
        top: position.top + this.reservedAdHeight(),
        bottom: position.bottom + rail
      }
    }
  }

  /**
   * Vertical space the ad strip is currently taking off the top of the media area.
   *
   * Read from .lg-content's own computed offset rather than a constant, so gallery.css
   * stays the single source of truth for the strip height, and a slot that never filled
   * (or collapsed after an empty fill) contributes zero without extra branching.
   *
   * @returns {number}
   */
  reservedAdHeight() {
    const content = document.querySelector(LG_CONTENT)
    if (!content) return 0

    return parseFloat(window.getComputedStyle(content).top) || 0
  }

  /**
   * Height of the thumbnail rail overlaying the bottom of the media area.
   *
   * `allowMediaOverlap: true` is what makes the rail float over the media rather than
   * shrink it, and it also stops lightGallery subtracting the rail's height. That is
   * survivable on its own — media is centred, so it only slips a little way under. Once
   * the ad takes height off the top, the centre moves down and the bottom of a video
   * lands behind the rail. Reporting the rail height moves the centre back.
   *
   * @returns {number}
   */
  thumbnailRailHeight() {
    const rail = document.querySelector(LG_THUMB_OUTER)
    if (!rail) return 0

    return rail.getBoundingClientRect().height || 0
  }

  /**
   * Re-run lightGallery's sizing after the strip appears or disappears.
   *
   * The ad fills asynchronously, so the first slide is laid out before the strip exists.
   * lightGallery only recalculates on slide change and window resize, so without this a
   * video opened as the first slide keeps the size it was given for the full viewport.
   */
  remeasureMedia() {
    if (typeof this.gallery?.refreshOnResize === "function") {
      this.gallery.refreshOnResize()
    }
  }
  
  onBeforeOpen() {
    this.isGalleryOpenEvent = true
  }
  
  onAfterOpen() {
    if (this.isVideo()) {
      setTimeout(() => {
        this.hideThumbs()
      }, 3000)
    }
    
    // Captured once per open rather than re-read on each resize: the creative that was
    // requested is fixed at open time, so the breakpoint that matters is the one that
    // was in force then.
    this.openedOnDesktop = this.isDesktop()

    if (this.hasSpaceForAds()) {
      const placement = this.galleryAdPlacement()
      placement?.classList?.remove("advertisement--empty")
      // Tells googletag#rendered the slot is still owned by an open gallery.
      if (placement) placement.dataset.slotLive = ""

      // The strip only exists once a creative lands, which is after this slide has been
      // laid out — see remeasureMedia(). googletag:resize is dispatched on the element
      // itself and does not bubble, so it has to be bound here rather than via Stimulus.
      if (placement) {
        this.remeasureOnFill = this.remeasureMedia.bind(this)
        placement.addEventListener("googletag:resize", this.remeasureOnFill)
      }

      window.document.dispatchEvent(new CustomEvent("gallery:showad"))
      this.announceSlot()
    }
  }

  /**
   * Tell the global ad refresh module that this slot now exists.
   *
   * The slot's element is in the DOM at page load but its GPT slot is only defined when
   * the gallery opens, so ad-refresh.js — which discovers on load and on DOM insertion —
   * never sees it. Announcing here rather than from googletag_controller#show keeps the
   * change desktop- and gallery-scoped: show() is not breakpoint-gated, and 320x50 is a
   * refresh-eligible size, so announcing there would put the mobile gallery ad on a
   * refresh timer for the first time — on inventory not declared as refreshing in GAM.
   *
   * Ordering is safe without a guard: the googletag controller's show() action runs
   * synchronously on gallery:showad and queues its defineSlot first; googletag.cmd is FIFO.
   */
  announceSlot() {
    if (!this.isDesktop()) return

    const elementId = this.galleryAdPlacement()?.id
    if (!elementId) return

    googletag.cmd.push(() => {
      window.document.dispatchEvent(new CustomEvent("googletag:slotdefined", {detail: {elementId}}))
    })
  }
  
  hideThumbs() {
    this.gallery.outer.firstElement.classList.remove("lg-components-open")
  }
  
  hasSpaceForAds() {
    // Desktop reserves a strip above the image (see gallery.css), so there is no
    // collision to avoid. Below that, the original portrait-viewport test is unchanged.
    if (this.isDesktop()) {
      return true
    }
    return this.aspectRatio() < 0.9
  }

  /**
   * Matches the `--layout-large` custom media in base.css. Uses matchMedia rather than
   * innerWidth so the JS gate and the CSS breakpoint cannot drift apart.
   */
  isDesktop() {
    return window.matchMedia("(min-width: 1024px)").matches
  }
  
  aspectRatio() {
    return window.innerWidth / window.innerHeight
  }
  
  isVideo() {
    const {index, galleryItems} = this.gallery
    return !!galleryItems[index].videoId
  }
  
  isPortraitImage() {
    if (this.isVideo()) {
      return false
    }
    const image = document.querySelector(".lg-current img")
    if (!image) {
      return false
    }
    return image.naturalHeight > image.naturalWidth
  }
  
  onAfterSlide(event) {
    const idx = event.detail.index
    virtualPageView({virtualPageUrl: window.location.pathname, virtualPageTitle: document.title})
    gtmTrack({
      event: this.isGalleryOpenEvent ? "galleryOpen" : "galleryImageChange", eventInfo: {
        galleryName: document.title, carInfo: {
          totalImages: this.dynamicElValue.length, image: this.dynamicElValue[idx].src, imageIndex: idx
        }
      }
    })
    this.isGalleryOpenEvent = false
  }
  
  onAfterItemLoad() {
    // On desktop the ad has its own reserved strip, so a portrait image can't collide
    // with it. Destroying here would kill the ad for the rest of the gallery session —
    // removeAd() has no counterpart that re-shows it.
    if (this.openedOnDesktop) {
      return
    }
    if (this.isPortraitImage()) {
      this.removeAd()
    }
  }
  
  removeAd() {
    const placement = this.galleryAdPlacement()
    placement?.classList?.add("advertisement--empty")
    placement?.classList?.remove("advertisement--filled")
    // Drops the reserved strip and stops a late slotRenderEnded re-showing the creative.
    if (placement) delete placement.dataset.slotLive

    if (placement && this.remeasureOnFill) {
      placement.removeEventListener("googletag:resize", this.remeasureOnFill)
      this.remeasureOnFill = undefined
    }

    window.document.dispatchEvent(new CustomEvent("googletag:destroy"))
  }
  
  beforeClose() {
    this.removeAd()
  }
  
  resize() {
    // Opened below the breakpoint: unchanged behaviour, drop the ad on any resize.
    if (!this.openedOnDesktop) {
      this.removeAd()
      return
    }

    // Opened on desktop: the strip is reserved, so a resize within desktop is not a
    // reason to destroy the slot — that's what keeps "renders once per gallery" true.
    // Crossing below the breakpoint is different: the 728x90 already in the slot no
    // longer fits, and nothing re-requests it at the smaller size.
    if (!this.isDesktop()) {
      this.removeAd()
    }
  }
}
